import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config, { EVENTS_CREATE_URL } from '../../config';
import { toast } from 'react-toastify';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';
import { EventCard } from './EventCard';
import { useObserver } from '../../hooks/useObserver';
import { apiEvents } from '../../api/apiEvents.api';
import { apiUser } from '../../api/apiUser.api';
import { optimisticToggleJoin } from '../../utils/eventJoin';

function Events() {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [searchParams] = useSearchParams();

  // Фильтры приходят через query: /events?city=Минск&sort=date&date=2026-10-25
  const city = searchParams.get('city') || '';
  const sort = searchParams.get('sort') || '';
  const date = searchParams.get('date') || '';

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [totalItems, setTotalItems] = useState(null);
  const [page, setPage] = useState(1);
  const isLoadingRef = useRef(false);
  const [ownAvatar, setOwnAvatar] = useState(null);

  // Свой аватар — для оптимистичного отображения в участниках
  useEffect(() => {
    apiUser
      .fetchMe(axiosPrivate)
      .then((data) => setOwnAvatar(data?.photos?.[0] || null))
      .catch(() => {});
  }, [axiosPrivate]);

  const fetchEvents = useCallback(
    async ({ nextPage = 1, replace = false } = {}) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      try {
        const data = await apiEvents.fetchEvents(axiosPrivate, {
          page: nextPage,
          limit: 20,
          city,
          sort,
          date
        });
        const items = data?.items || [];

        setEvents((prev) => {
          if (replace) return items;

          const next = new Map(prev.map((event) => [event._id, event]));
          items.forEach((event) => next.set(event._id, event));
          return Array.from(next.values());
        });
        setTotalItems(data?.total ?? items.length);
        setPage(nextPage);
      } catch (error) {
        console.error('Error fetching events:', error);
        toast.error('Не удалось загрузить мероприятия');
      } finally {
        isLoadingRef.current = false;
      }
    },
    [axiosPrivate, city, sort, date]
  );

  const targetRef = useObserver({
    onIntersecting: () => {
      if (isLoadingRef.current || events.length === 0 || totalItems === null || events.length >= totalItems) {
        return;
      }

      fetchEvents({ nextPage: page + 1 });
    },
    rootMargin: '100px 0px 0px 0px'
  });

  useEffect(() => {
    fetchEvents({ nextPage: 1, replace: true }).finally(() => setIsLoading(false));
  }, [fetchEvents]);

  const handleOpen = (id) => navigate(config.ROUTES.EVENTS.DETAIL(id));

  const handleInvite = (event) => navigate(config.ROUTES.EVENTS.INVITE(event._id));

  const handleJoin = async (event) => {
    const willJoin = !event.isJoined;

    // Оптимистично: статус, счётчик и аватары меняются мгновенно
    setEvents((prev) =>
      prev.map((e) => (e._id === event._id ? optimisticToggleJoin(e, willJoin, ownAvatar) : e))
    );

    try {
      await apiEvents.toggleJoin(axiosPrivate, event._id);
      toast.success(willJoin ? 'Вы отметились: «Хочу пойти»!' : 'Участие отменено');

      // Тихо подтягиваем актуальные данные карточки с сервера
      apiEvents
        .fetchEvent(axiosPrivate, event._id)
        .then((data) => {
          setEvents((prev) => prev.map((e) => (e._id === event._id ? data : e)));
        })
        .catch(() => {});
    } catch (error) {
      setEvents((prev) => prev.map((e) => (e._id === event._id ? event : e)));
      toast.error(error?.response?.data?.message || 'Не удалось сохранить ответ');
    }
  };

  const hasFilters = Boolean(city || sort || date);

  return (
    <div className="flex flex-col items-center w-full h-full overflow-hidden">
      {/* Кнопка создания — ведёт в кабинет организатора */}
      <a
        href={EVENTS_CREATE_URL}
        target="_blank"
        rel="noreferrer"
        className="w-full flex items-center justify-between gap-2 mx-4 mt-[12px] h-[48px] rounded-[12px] bg-[#1A2727] border border-[#A1F69E] text-[#A1F69E] text-[18px] font-normal font-raleway shrink-0 px-4"
        style={{ maxWidth: 'calc(100% - 32px)' }}
      >
        Создать мероприятие
        <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M-7.53403e-05 5.4082H12.3076M7.30762 0.408203L12.3076 5.4082L7.30762 10.4082"
            stroke="#A1F69E"
            stroke-width="1.15385"
          ></path>
        </svg>
      </a>

      {isLoading && events.length <= 0 && (
        <div className="flex items-center justify-center flex-1">
          <DotSpinner />
        </div>
      )}
      {!isLoading && events.length <= 0 && (
        <div className="flex flex-col items-center justify-center flex-1 h-full px-4">
          {hasFilters ? (
            <>
              <img src="/icons/meet-oops.svg" alt="meet-oops" className="w-[100px]" />
              <p className="text-center font-semibold text-[18px] text-white mt-3">
                Ой, по этому запросу пока ничего не нашлось
              </p>
              <button
                onClick={() => navigate(config.ROUTES.EVENTS.LIST)}
                className="w-max mt-6 h-[48px] px-6 rounded-[400px] bg-[#A1F69E] text-[#010D0D] text-[15px] font-semibold font-raleway active:bg-[#7DD279]"
              >
                Сбросить фильтр
              </button>
            </>
          ) : (
            <>
              <div className="w-[100px] h-[100px] rounded-full bg-[#022424] flex items-center justify-center">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 4H17V2H15V4H9V2H7V4H5C3.897 4 3 4.897 3 6V20C3 21.103 3.897 22 5 22H19C20.103 22 21 21.103 21 20V6C21 4.897 20.103 4 19 4ZM19 20H5V9H19V20Z"
                    fill="#A1F69E"
                  />
                </svg>
              </div>
              <p className="text-center font-semibold text-[18px] text-white mt-3">
                Скоро здесь появятся мероприятия из вашего города
              </p>
            </>
          )}
        </div>
      )}

      {events.length > 0 && (
        <div className="flex-1 w-full overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 relative w-full px-4 pt-[12px] pb-[130px]">
            {events.map((event) => (
              <EventCard
                key={event._id}
                event={event}
                onOpen={handleOpen}
                onJoin={handleJoin}
                onInvite={handleInvite}
              />
            ))}
          </div>
        </div>
      )}
      <div ref={targetRef} className="h-2 shrink-0" />
    </div>
  );
}

export default Events;
